// src/pages/AdminApiKeys.tsx
// Sunucu-sunucu API anahtarı yönetimi (ör. BringGo kendi backend'inden çağırır).
// Anahtar yalnızca oluşturma anında bir kez gösterilir; sonra sadece hash'i saklanır.
import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, RefreshCw, ShieldOff, ShieldCheck, X, Copy, Activity, AlertTriangle, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import type { ApiKeyRow, ApiKeyUsageStats } from '../services/api';

function fmt(iso?: string | null): string {
  if (!iso) return '—';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function AdminApiKeys() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(false);

  // oluşturma
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('BringGo');
  const [userEmail, setUserEmail] = useState('bringgoship@gmail.com');
  const [rateLimit, setRateLimit] = useState(2000);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);

  // düzenleme (ad / saatlik limit / not)
  const [editing, setEditing] = useState<ApiKeyRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editLimit, setEditLimit] = useState(0);
  const [editNote, setEditNote] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // kullanım
  const [usageFor, setUsageFor] = useState<ApiKeyRow | null>(null);
  const [usage, setUsage] = useState<ApiKeyUsageStats | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getApiKeys();
      setKeys(res.keys || []);
    } catch (e: any) { toast.error(e?.message || 'Anahtarlar yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim() || !userEmail.trim()) { toast.error('Ad ve hesap e-postası gerekli'); return; }
    setCreating(true);
    try {
      const res = await apiService.createApiKey({
        name: name.trim(), userEmail: userEmail.trim(),
        rateLimitPerHour: rateLimit, note: note.trim() || undefined
      });
      setNewKey(res.apiKey);
      setShowForm(false);
      setNote('');
      load();
    } catch (e: any) { toast.error(e?.message || 'Anahtar oluşturulamadı'); }
    finally { setCreating(false); }
  };

  const revoke = async (k: ApiKeyRow) => {
    if (!window.confirm(`"${k.name}" anahtarı iptal edilecek. Bu anahtarı kullanan sistem ANINDA durur. Emin misiniz?`)) return;
    try { await apiService.revokeApiKey(k.id); toast.success('Anahtar iptal edildi'); load(); }
    catch (e: any) { toast.error(e?.message || 'İptal edilemedi'); }
  };

  const activate = async (k: ApiKeyRow) => {
    try { await apiService.activateApiKey(k.id); toast.success('Anahtar etkinleştirildi'); load(); }
    catch (e: any) { toast.error(e?.message || 'Etkinleştirilemedi'); }
  };

  const openEdit = (k: ApiKeyRow) => {
    setEditing(k);
    setEditName(k.name);
    setEditLimit(k.rateLimitPerHour);
    setEditNote(k.note || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editName.trim()) { toast.error('Ad boş olamaz'); return; }
    setSavingEdit(true);
    try {
      await apiService.updateApiKey(editing.id, {
        name: editName.trim(),
        rateLimitPerHour: editLimit,
        note: editNote.trim()
      });
      toast.success('Güncellendi (en geç 60 sn içinde etkili olur)');
      setEditing(null);
      load();
    } catch (e: any) { toast.error(e?.message || 'Güncellenemedi'); }
    finally { setSavingEdit(false); }
  };

  const openUsage = async (k: ApiKeyRow) => {
    setUsageFor(k); setUsage(null); setUsageLoading(true);
    try { setUsage(await apiService.getApiKeyUsage(k.id, 7)); }
    catch (e: any) { toast.error(e?.message || 'Kullanım verisi alınamadı'); }
    finally { setUsageLoading(false); }
  };

  const copy = (t: string) => {
    navigator.clipboard?.writeText(t).then(() => toast.success('Kopyalandı')).catch(() => {});
  };

  return (
    <div className="space-y-4">
      {/* Üst çubuk */}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setShowForm(v => !v)}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
          <Plus size={16} /> Yeni Anahtar
        </button>
        <button onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Yenile
        </button>
      </div>

      {/* Oluşturma formu */}
      {showForm && (
        <div className="rounded-xl border border-indigo-200 bg-white p-4">
          <h3 className="mb-3 font-semibold text-gray-800">Yeni API Anahtarı</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500">Ad</label>
              <input value={name} onChange={e => setName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Hangi hesap adına çalışacak</label>
              <input value={userEmail} onChange={e => setUserEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Saatlik istek limiti</label>
              <input type="number" value={rateLimit} onChange={e => setRateLimit(parseInt(e.target.value || '0', 10))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500">Not (opsiyonel)</label>
              <input value={note} onChange={e => setNote(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
            </div>
          </div>
          <p className="mt-3 rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
            Yetki <span className="font-semibold">otomatik olarak dar</span>: sadece <code>creators/get-link</code> ve <code>orders/scrape</code>.
            Kazanç, ödeme ve hesap uçlarına erişemez.
          </p>
          <div className="mt-3 flex gap-2">
            <button onClick={create} disabled={creating}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
              {creating ? 'Oluşturuluyor…' : 'Oluştur'}
            </button>
            <button onClick={() => setShowForm(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
          </div>
        </div>
      )}

      {/* Yeni anahtar — tek gösterim */}
      {newKey && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle size={18} /> Anahtar yalnızca ŞİMDİ gösteriliyor
          </div>
          <p className="mb-2 text-sm text-amber-800">
            Bu değeri güvenli bir yere kaydedin — bir daha görüntülenemez. Karşı tarafa iletirken
            <span className="font-semibold"> kendi sunucularında </span> saklamalarını şart koşun.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-lg bg-white p-3 font-mono text-sm text-gray-800">{newKey}</code>
            <button onClick={() => copy(newKey)} className="rounded-lg bg-amber-600 p-2.5 text-white hover:bg-amber-700"><Copy size={16} /></button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-3 text-sm text-amber-800 underline">Kaydettim, gizle</button>
        </div>
      )}

      {/* Liste */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3 font-medium">Ad</th>
              <th className="px-4 py-3 font-medium">Anahtar</th>
              <th className="px-4 py-3 font-medium">Hesap</th>
              <th className="px-4 py-3 font-medium">Durum</th>
              <th className="px-4 py-3 font-medium">Limit/saat</th>
              <th className="px-4 py-3 font-medium">Son kullanım</th>
              <th className="px-4 py-3 text-center font-medium">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && keys.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Yükleniyor…</td></tr>
            ) : keys.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Henüz anahtar yok.</td></tr>
            ) : keys.map(k => (
              <tr key={k.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{k.name}</div>
                  {k.note && <div className="text-xs text-gray-500">{k.note}</div>}
                </td>
                <td className="px-4 py-3"><code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{k.keyPrefix}…</code></td>
                <td className="px-4 py-3 text-xs text-gray-600">{k.userEmail}</td>
                <td className="px-4 py-3">
                  {k.isActive
                    ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Aktif</span>
                    : <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">İptal</span>}
                </td>
                <td className="px-4 py-3 text-xs tabular-nums text-gray-700">{k.rateLimitPerHour === 0 ? 'sınırsız' : k.rateLimitPerHour}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{fmt(k.lastUsedAtUtc)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1.5">
                    <button onClick={() => openEdit(k)} title="Düzenle (ad / limit / not)"
                      className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"><Pencil size={16} /></button>
                    <button onClick={() => openUsage(k)} title="Kullanım / anormallik"
                      className="rounded-lg p-2 text-indigo-600 hover:bg-indigo-50"><Activity size={16} /></button>
                    {k.isActive
                      ? <button onClick={() => revoke(k)} title="İptal et" className="rounded-lg p-2 text-rose-600 hover:bg-rose-50"><ShieldOff size={16} /></button>
                      : <button onClick={() => activate(k)} title="Yeniden etkinleştir" className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50"><ShieldCheck size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Düzenleme modalı */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Anahtarı Düzenle</h3>
              <button onClick={() => setEditing(null)} className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-gray-500">Ad</label>
                <input value={editName} onChange={e => setEditName(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Saatlik istek limiti (0 = sınırsız)</label>
                <input type="number" min={0} value={editLimit}
                  onChange={e => setEditLimit(parseInt(e.target.value || '0', 10))}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Not</label>
                <input value={editNote} onChange={e => setEditNote(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
              </div>
              <p className="rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
                Anahtarın kendisi değiştirilemez. Yeni bir anahtar gerekiyorsa bunu iptal edip yenisini oluşturun.
              </p>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={saveEdit} disabled={savingEdit}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
                {savingEdit ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
              <button onClick={() => setEditing(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* Kullanım paneli */}
      {usageFor && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setUsageFor(null)} />
          <div className="relative flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">{usageFor.name} — Kullanım (7 gün)</h2>
                <p className="font-mono text-[11px] text-gray-400">{usageFor.keyPrefix}…</p>
              </div>
              <button onClick={() => setUsageFor(null)} className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            </div>

            {usageLoading ? (
              <div className="p-10 text-center text-gray-400">Yükleniyor…</div>
            ) : !usage ? (
              <div className="p-10 text-center text-gray-400">Veri yok.</div>
            ) : (
              <div className="space-y-4 p-5">
                {/* EN ÖNEMLİ GÖSTERGE */}
                <div className={`rounded-xl border-2 p-4 ${
                  usage.distinctIps === 0 ? 'border-gray-200 bg-gray-50'
                  : usage.distinctIps <= 3 ? 'border-emerald-300 bg-emerald-50'
                  : usage.distinctIps <= 10 ? 'border-amber-300 bg-amber-50'
                  : 'border-rose-300 bg-rose-50'}`}>
                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Farklı IP sayısı</div>
                  <div className="mt-1 text-3xl font-bold tabular-nums text-gray-900">{usage.distinctIps}</div>
                  <p className="mt-1 text-sm">
                    {usage.distinctIps === 0 ? 'Henüz kullanım yok.'
                      : usage.distinctIps <= 3 ? '✅ Tek noktadan geliyor — anahtar karşı tarafın sunucusunda (istenen durum).'
                      : usage.distinctIps <= 10 ? '⚠️ Birden fazla nokta. Birkaç sunucu olabilir; takip edin.'
                      : '🚨 Çok sayıda farklı IP — anahtar son kullanıcılara dağıtılmış olabilir (ör. eklentiye gömülmüş).'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">Toplam istek</div>
                    <div className="mt-1 text-xl font-bold tabular-nums">{usage.total}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="text-xs text-gray-500">Saatlik limit</div>
                    <div className="mt-1 text-xl font-bold tabular-nums">{usageFor.rateLimitPerHour}</div>
                  </div>
                </div>

                {usage.byPath.length > 0 && (
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Uçlara göre</div>
                    <div className="rounded-lg border border-gray-200">
                      {usage.byPath.map((p, i) => (
                        <div key={i} className="flex justify-between border-b border-gray-100 px-3 py-2 text-xs last:border-0">
                          <span className="font-mono text-gray-700">{p.path || '—'}</span>
                          <span className="tabular-nums font-semibold">{p.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {usage.byStatus.length > 0 && (
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Sonuçlara göre</div>
                    <div className="flex flex-wrap gap-2">
                      {usage.byStatus.map((s, i) => (
                        <span key={i} className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                          s.status === 200 ? 'bg-emerald-100 text-emerald-700'
                          : s.status === 429 ? 'bg-amber-100 text-amber-700'
                          : 'bg-rose-100 text-rose-700'}`}>
                          {s.status}: {s.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {usage.topIps.length > 0 && (
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">En çok kullanan IP'ler</div>
                    <div className="rounded-lg border border-gray-200">
                      {usage.topIps.map((t, i) => (
                        <div key={i} className="flex justify-between border-b border-gray-100 px-3 py-2 text-xs last:border-0">
                          <span className="font-mono text-gray-700">{t.ip}</span>
                          <span className="tabular-nums text-gray-500">{t.count} · {fmt(t.last)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
